import type { StructureDef } from '@playnets/sim';

/**
 * Tiny inline SVG marks, one per structure kind and one per planet power.
 *
 * Icons rather than names: the dialogs have to fit on a phone next to a live
 * playfield, and a grid of six labelled cards is a wall of reading. The shapes
 * deliberately echo how the thing looks once it's mounted, so recognising it in
 * the dialog and recognising it on the crust are the same act.
 */
export function StructureIcon({ def }: { def: StructureDef }): JSX.Element {
  const c = `#${def.colour.toString(16).padStart(6, '0')}`;
  return (
    <svg viewBox="0 0 32 32" className="icon" aria-hidden="true">
      {def.kind === 'turret' && (
        <>
          <rect x="8" y="16" width="16" height="11" rx="2" fill={c} />
          <rect x="14" y="4" width="4" height="13" fill={c} />
        </>
      )}
      {def.kind === 'beam' && (
        <>
          <path d="M10 27 L22 27 L19 6 L13 6 Z" fill={c} />
          <circle cx="16" cy="22" r="4" fill={c} opacity="0.6" />
        </>
      )}
      {def.kind === 'moon' && (
        <>
          <circle cx="16" cy="16" r="11" fill={c} />
          <circle cx="12" cy="12" r="2.6" fill="#5b616d" opacity="0.8" />
          <circle cx="20" cy="19" r="3.4" fill="#5b616d" opacity="0.8" />
        </>
      )}
      {def.kind === 'mine' && (
        <>
          <circle cx="9" cy="20" r="4.5" fill={c} />
          <circle cx="16" cy="11" r="4.5" fill={c} />
          <circle cx="23" cy="20" r="4.5" fill={c} />
        </>
      )}
      {def.kind === 'support' && (
        <>
          <path d="M16 4 L27 16 L16 28 L5 16 Z" fill={c} opacity="0.9" />
          <circle cx="16" cy="16" r="4" fill="#11202c" opacity="0.45" />
        </>
      )}
      {def.kind === 'drone-bay' && (
        <>
          <rect x="6" y="14" width="20" height="13" rx="2" fill={c} />
          <rect x="9" y="5" width="5" height="9" fill={c} opacity="0.7" />
          <rect x="18" y="5" width="5" height="9" fill={c} opacity="0.7" />
        </>
      )}
    </svg>
  );
}

/** One mark per planet power. */
export function UpgradeIcon({ id }: { id: string }): JSX.Element {
  const c = 'var(--hot)';
  return (
    <svg viewBox="0 0 32 32" className="icon" aria-hidden="true">
      {id === 'hull' && (
        <path d="M16 3 L27 8 V17 C27 23 22 27 16 29 C10 27 5 23 5 17 V8 Z" fill={c} />
      )}
      {id === 'magnet' && (
        <>
          <path
            d="M8 26 V15 A8 8 0 0 1 24 15 V26"
            fill="none"
            stroke={c}
            strokeWidth="5"
            strokeLinecap="round"
          />
          <rect x="5.5" y="23" width="5" height="5" fill="#fff" opacity="0.7" />
          <rect x="21.5" y="23" width="5" height="5" fill="#fff" opacity="0.7" />
        </>
      )}
      {id === 'mint' && (
        <>
          <circle cx="16" cy="16" r="11" fill={c} />
          <circle cx="12" cy="12" r="3" fill="#fff" opacity="0.65" />
        </>
      )}
      {id === 'servos' && (
        <>
          <circle cx="16" cy="16" r="7" fill="none" stroke={c} strokeWidth="4" />
          <path d="M16 3 L21 9 H11 Z" fill={c} />
          <path d="M16 29 L11 23 H21 Z" fill={c} />
        </>
      )}
      {id === 'crust-mounts' && (
        <>
          <circle cx="16" cy="16" r="9" fill="none" stroke={c} strokeWidth="3" />
          <rect x="14" y="1" width="4" height="8" fill={c} />
        </>
      )}
      {id === 'orbit-mounts' && (
        <>
          <ellipse cx="16" cy="16" rx="13" ry="6" fill="none" stroke={c} strokeWidth="3" />
          <circle cx="29" cy="16" r="3.5" fill={c} />
        </>
      )}
    </svg>
  );
}
